<template>
	<section class="space-y-2 sm:container sm:mt-2" aria-label="Přehled akcí a novinek">
		<CarouselSkeleton
			:key="carouselKey"
			ref="carouselRef"
			:items="listData"
			list-class="inline-flex w-full overflow-hidden sm:rounded-lg sm:gap-3"
			calculate-list-width-from-item-count
			always-active
			page-per-item
			snap-items
			infinite
			autoplay
			:pause-autoplay="isNavigationFocused || isSlideItemFocused"
			@index-update="(index) => currentIndex = index"
		>
			<template #item="{item, index}">
				<li :key="item._uid" class="w-full shrink-0">
					<StoryblokComponent
						:blok="item"
						@item-focus="() => handleSlideItemFocus(index)"
						@item-blur="handleSlideItemBlur"
					/>
				</li>
			</template>
			<template #inner="{seek, current, total, increment, decrement}">
				<nav
					class="absolute bottom-1.5 left-1/2 -translate-x-1/2 inline-flex gap-x-2 lg:hidden"
					aria-hidden="true"
				>
					<button
						v-for="(index1, index0) in total"
						:key="index0"
						type="button"
						aria-label="Přejít na další"
						class="relative rounded-full w-2.5 h-2.5 before:absolute before:-inset-1 transition-colors"
						:class="
							index1 === current
								? 'bg-background-important border-border-highlighted'
								: 'bg-white border-border-tertiary'
						"
						@click="seek(index0)"
						@focus="handleNavigationFocus"
						@blur="handleNavigationBlur"
					/>
				</nav>
				<button
					ref="prevButtonRef"
					class="btn btn-secondary btn-icon absolute top-1/2 -translate-y-1/2 left-2"
					type="button"
					aria-label="Předchozí"
					@click="decrement"
					@focus="handleNavigationFocus"
					@blur="handleNavigationBlur"
				>
					<NuxtIcon name="chevron-left" filled />
				</button>
				<button
					ref="nextButtonRef"
					class="btn btn-secondary btn-icon absolute top-1/2 -translate-y-1/2 right-2"
					type="button"
					aria-label="Další"
					@click="increment"
					@focus="handleNavigationFocus"
					@blur="handleNavigationBlur"
				>
					<NuxtIcon name="chevron-right" filled />
				</button>
			</template>
		</CarouselSkeleton>

		<nav class="max-sm:container" aria-label="Sekundární seznam akcí a novinek">
			<ul class="grid grid-flow-col auto-cols-fr gap-2">
				<li
					v-for="item in secondaryListData"
					:key="item._uid"
				>
					<StoryblokComponent :blok="item" />
				</li>
			</ul>
		</nav>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {snakeCase} from 'lodash-es';
	import {useGtm} from '@gtm-support/vue-gtm';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {isStoryblokStory} from '~/utils/stories';
	import type {
		HomepageIntroItemStoryblok,
		HomepageIntroStoryblok,
	} from '~/storyblok/types';

	type Props = {
		blok: HomepageIntroStoryblok;
	};

	const props = defineProps<Props>();

	const gtm = useGtm();
	const currentIndex = ref(0);
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);
	const trackedIndices = new Set<number>();
	const isNavigationFocused = ref(false);
	const isSlideItemFocused = ref(false);
	const carouselRef = ref();
	const carouselKey = ref(0);
	let focusTimeoutId: ReturnType<typeof setTimeout> | null = null;
	let slideItemFocusTimeoutId: ReturnType<typeof setTimeout> | null = null;
	const personalizedListData = computed(() => (selectedHypermarket.value
		? props.blok.personalizedList?.find((item) => (
			item.hypermarket?.some((hm) => isStoryblokStory(hm) && hm.slug === selectedHypermarket.value)
		))?.list
		: null));
	const personalizedSecondaryListData = computed(() => (selectedHypermarket.value
		? props.blok.personalizedSecondaryList?.find((item) => (
			item.hypermarket.some((hm) => isStoryblokStory(hm) && hm.slug === selectedHypermarket.value)
		))
		: null));
	const listData = computed<HomepageIntroItemStoryblok[]>(() => personalizedListData.value?.length
		? personalizedListData.value
		: props.blok.list);
	const secondaryListData = computed(() => personalizedSecondaryListData.value?.secondaryList ?? props.blok.secondaryList ?? []);

	watch(listData, () => {
		// Recreate GSAP animations for the new slide elements, even if their count stays the same.
		carouselKey.value++;
		currentIndex.value = 0;
		trackedIndices.clear();
		isNavigationFocused.value = false;
		isSlideItemFocused.value = false;
		if (focusTimeoutId) {
			clearTimeout(focusTimeoutId);
			focusTimeoutId = null;
		}
		if (slideItemFocusTimeoutId) {
			clearTimeout(slideItemFocusTimeoutId);
			slideItemFocusTimeoutId = null;
		}
	});

	function trackCarousel(event: string) {
		// Don't track if the slide has already been tracked
		if (trackedIndices.has(currentIndex.value)) {
			return;
		}

		const slide = listData.value[currentIndex.value];
		if (!slide) {
			return;
		}

		const slideImage = slide.image;
		if (!slideImage) {
			return;
		}

		const slideName = slideImage.name
			|| slideImage.title
			|| slideImage.alt
			|| slideImage.filename?.match(/\/([-\w\s]+)\.[a-z]+$/)?.[1];

		gtm?.push({
			event,
			ecommerce: {
				promotion_id: slideName ? snakeCase(slideName) : slideImage.id,
				promotion_name: slideName,
				counter: currentIndex.value,
				page_type: 'homepage',
			},
		});

		trackedIndices.add(currentIndex.value);
	}

	function handleNavigationFocus() {
		if (focusTimeoutId) {
			clearTimeout(focusTimeoutId);
			focusTimeoutId = null;
		}
		isNavigationFocused.value = true;
	}

	function handleNavigationBlur() {
		focusTimeoutId = setTimeout(() => {
			isNavigationFocused.value = false;
			focusTimeoutId = null;
		}, 150);
	}

	function handleSlideItemFocus(itemIndex: number) {
		if (slideItemFocusTimeoutId) {
			clearTimeout(slideItemFocusTimeoutId);
			slideItemFocusTimeoutId = null;
		}

		isSlideItemFocused.value = true;

		if (currentIndex.value !== itemIndex && carouselRef.value) {
			carouselRef.value.seek?.(itemIndex, true);
		}
	}

	function handleSlideItemBlur() {
		slideItemFocusTimeoutId = setTimeout(() => {
			isSlideItemFocused.value = false;
			slideItemFocusTimeoutId = null;
		}, 150);
	}

	if (import.meta.client && window?.dataLayer) {
		watch([currentIndex, carouselKey], async () => {
			await nextTick();
			setTimeout(() => {
				trackCarousel('view_promotion');
			}, 0);
		}, {immediate: true});
	}

	onUnmounted(() => {
		if (focusTimeoutId) {
			clearTimeout(focusTimeoutId);
		}
		if (slideItemFocusTimeoutId) {
			clearTimeout(slideItemFocusTimeoutId);
		}
	});
</script>
